/**
 * Data model for the Pre-Lab pump test stand.
 *
 * The test stand is a closed-loop pump test rig: water circulates from an
 * open tank through suction piping → pump → discharge piping → throttle
 * valve → back to the tank. Pressure gauges at the pump suction and
 * discharge taps let students calculate pump head:
 *
 *   H = (P_d − P_s) / (ρ · g)
 *
 * The hidden pump curve  Hp(Q) = a − b·Q²  is NOT revealed to students.
 * They must determine it experimentally by varying the valve opening,
 * reading gauge pressures, and computing head at each flow rate.
 */

export const pumpTestStand = {
  /* ── Hidden pump curve (students must derive this) ────────────── */
  trueCurve: {
    a: 30, // m   – shutoff head
    b: 950000 // m/(m³/s)²  – curvature coefficient
    // Hp(Q) = 30 − 950 000·Q²
    // Max theoretical Q (Hp = 0) ≈ 0.00562 m³/s ≈ 89 GPM
  },

  /* ── Pipe (same diameter for suction & discharge) ─────────────── */
  pipe: {
    diameter_m: 0.0508, // 2-inch nominal schedule-40
    roughness_m: 0.000045 // commercial steel
  },

  /* ── Suction side (tank → pump inlet) ─────────────────────────── */
  suction: {
    length_m: 2,
    K: 1.5, // entrance loss 0.5 + 90° elbow 1.0
    elevationAboveTank_m: 1 // pump centreline above tank water surface
  },

  /* ── Discharge side (pump outlet → valve → tank return) ───────── */
  discharge: {
    length_m: 10,
    K: 8.5 // 2 elbows (2×1.0) + tee (1.8) + exit (1.0) + misc (3.7)
  },

  /* ── Throttle valve (equal-percentage characteristic) ─────────── */
  valve: {
    Kopen: 3, // minor-loss K when fully open
    rangeability: 50 // equal-percentage R parameter
    // K(x) = Kopen · R^(2·(1−x))  where x = opening fraction [0, 1]
  },

  /* ── Test fluid: water at ≈ 20 °C ────────────────────────────── */
  fluid: {
    density_kgm3: 998.2,
    viscosity_Pas: 0.001002
  },

  /* ── Gauge noise parameters ───────────────────────────────────── */
  noise: {
    pressure_kPa: 1.5, // ± on each gauge reading
    flow_fraction: 0.02 // ±2 % of true Q
  },

  /* ── Suggested valve positions for data collection ────────────── */
  recommendedPositions: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],

  /* ── Settling animation duration (ms) ─────────────────────────── */
  settlingTime_ms: 2000
};
